import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import './PrivacyPolicyPage.css';

// showBackButton: only passed true when embedded as a "My Profile" sidenav
// section (see ProfilePage.js/SpProfilePage.js) — the standalone /privacy
// route (linked from the public footer) keeps its plain marketing header.
const PrivacyPolicyPage = ({ showBackButton = false }) => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="privacy-policy-page">
      <section className="page-header">
        <div className="container">
          {showBackButton && (
            <button className="privacy-back-btn" onClick={() => navigate(-1)}>
              <span>←</span>
              <span>Back</span>
            </button>
          )}
          <h1>Privacy Policy</h1>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="policy-content">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Rialty.ai ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
            </p>

            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We may collect the following types of personal information:</p>
            <ul>
              <li>Name, email address, phone number, and contact details</li>
              <li>Professional information (real estate license number, brokerage affiliation)</li>
              <li>Account credentials and authentication information</li>
              <li>Transaction and property-related data</li>
              <li>Communication and correspondence records</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you use our platform, we automatically collect:</p>
            <ul>
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Usage data (pages viewed, features used, time spent on platform)</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Log data and analytics information</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <p>We use your information for the following purposes:</p>
            <ul>
              <li>To provide, maintain, and improve our services</li>
              <li>To process transactions and manage real estate workflows</li>
              <li>To communicate with you about your account and transactions</li>
              <li>To send notifications, updates, and administrative messages</li>
              <li>To detect, prevent, and address fraud or security issues</li>
              <li>To comply with legal obligations and enforce our terms</li>
              <li>To analyze usage patterns and improve user experience</li>
            </ul>

            <h2>4. Information Sharing and Disclosure</h2>
            <p>We may share your information with:</p>
            <ul>
              <li><b>Transaction Participants:</b> Other parties involved in your real estate transactions (agents, brokers, buyers, sellers)</li>
              <li><b>Service Providers:</b> Third-party vendors who assist in operating our platform</li>
              <li><b>Legal Requirements:</b> When required by law, court order, or government regulations</li>
              <li><b>Business Transfers:</b> In connection with mergers, acquisitions, or asset sales</li>
            </ul>
            <p>We do not sell your personal information to third parties.</p>

            <h2>5. SMS / Text Messaging</h2>
            <p>
              By providing your phone number, you consent to receive transactional text messages from Rialty, including invitations to join a transaction workspace and one-time verification codes to confirm your identity before viewing or signing documents. Message frequency varies based on your transaction activity. Message and data rates may apply. No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Reply STOP to opt out at any time, or HELP for help.
            </p>

            <h2>5A. Connecting Your Gmail Account (Google User Data)</h2>
            <p>
              If you choose to connect your Gmail account in Settings &rarr; Connected Services, Rialty uses Google's sign-in and permission process (OAuth). You decide whether to connect, and you can disconnect at any time.
            </p>
            <h3>What Google data we access</h3>
            <p>When you connect, Google gives Rialty:</p>
            <ul>
              <li>the email address of the Google account you connected, and</li>
              <li>a permission token that lets Rialty send email from that account on your behalf.</li>
            </ul>
            <p>
              We request send-only access (the <code>gmail.send</code> permission). Rialty does not read, list, search, download, modify, or delete any message in your Gmail account, and does not access your Gmail contacts or settings.
            </p>
            <h3>How we use it</h3>
            <p>
              We use this access only to send an email from your own Gmail address when you ask us to. For example, when you send a message to a contact from the CRM, send an offer package, or approve an email that Ria, our assistant, has drafted for you. We do not send anything you have not asked for or approved, and we do not use this access for any other purpose.
            </p>
            <h3>Limited use</h3>
            <p>
              Rialty's use and transfer to any other app of information received from Google APIs will adhere to the{' '}
              <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.
            </p>
            <h3>What we do not do</h3>
            <ul>
              <li>We do not sell Google user data.</li>
              <li>We do not use it for advertising or marketing.</li>
              <li>We do not use it to train artificial-intelligence or machine-learning models.</li>
              <li>We do not allow people at Rialty to read it, except with your consent, for security or abuse investigation, to comply with the law, or where it is needed to provide the feature you asked for.</li>
              <li>We do not send your Gmail address or permission token to AI providers.</li>
            </ul>
            <h3>What we store and how we protect it</h3>
            <p>
              We store your connected Gmail address and your permission tokens. The tokens are encrypted before they are stored and are never shown to you or to other users. Emails you send through Rialty are also saved, as your own record, on the relevant contact's timeline in your CRM, and that record is encrypted.
            </p>
            <h3>Who we share it with</h3>
            <p>
              Your permission tokens are used only to communicate with Google's servers in order to send your email. We do not share your Google user data with other third parties, except service providers who process it only on our behalf under agreements that meet Google's requirements, or where required by law.
            </p>
            <h3>Disconnecting and deleting your data</h3>
            <ul>
              <li><b>In Rialty:</b> go to Settings &rarr; Connected Services and select Disconnect. We delete your stored tokens and ask Google to revoke them.</li>
              <li><b>In Google:</b> you can also remove Rialty's access at any time in your Google Account under Security &rarr; Third-party apps with account access.</li>
              <li><b>Deletion requests:</b> to ask us to delete other data connected with your Gmail account, contact us at privacy@rialty.ai.</li>
              <li><b>If you stop using Rialty:</b> when your account is closed, we delete your stored tokens.</li>
            </ul>

            <h2>6. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information, including encryption, secure servers, and access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2>7. Your Rights and Choices</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access, update, or delete your personal information</li>
              <li>Opt-out of marketing communications</li>
              <li>Disable cookies through your browser settings</li>
              <li>Request a copy of your data</li>
              <li>Withdraw consent for data processing (where applicable)</li>
            </ul>

            <h2>8. Data Retention</h2>
            <p>
              We retain your information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Transaction data may be retained for regulatory compliance purposes.
            </p>

            <h2>9. Children's Privacy</h2>
            <p>
              Our platform is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>

            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
            </p>

            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on our platform and updating the "Last Updated" date. Your continued use of the platform after changes constitutes acceptance of the updated policy.
            </p>

            <h2>12. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="contact-info">
              <b>Rialty.ai</b><br />
              Email: privacy@rialty.ai<br />
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
